// src/screens/MuroScreen.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import {
    collection, query, where, orderBy, onSnapshot, doc,
    updateDoc, addDoc, getDocs, serverTimestamp, increment,
    arrayUnion, arrayRemove
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import {
    IoAdd,
    IoTimeOutline,
    IoCheckmarkCircleOutline,
    IoCloseCircleOutline,
    IoAlertCircleOutline,
    IoArrowBack,
    IoChatbubblesOutline,
    IoTrashOutline,
    IoListOutline,
    IoChatbubbleEllipsesOutline,
    IoLocationOutline,
    IoAppsOutline,
    IoDocumentTextOutline,
    IoHeartOutline,
    IoHeart,
    IoStorefrontOutline,
    IoWalletOutline,
    IoHomeOutline,
    IoSettingsOutline,
    IoWarningOutline,
    IoCashOutline,
    IoPeopleOutline,
    IoLogOutOutline,
} from 'react-icons/io5';
import moment from 'moment';
import 'moment/locale/es';
import './MuroScreen.css';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import BotonPanico from '../components/Safety/BotonPanico';
import CrearPostModal from './CrearPostModal';
import MuroChatList from './MuroChatList';
import MuroChatWindow from './MuroChatWindow';

// Badge Component
const CategoryBadge = ({ type }) => {
    let className = 'badge-aviso';
    if (type === 'venta') className = 'badge-venta';
    if (type === 'servicio') className = 'badge-servicio';
    if (type === 'evento') className = 'badge-evento';
    return <span className={`post-badge ${className}`}>{type}</span>;
};

const MuroScreen = () => {
    const { currentUser, userData, logout, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('comunidad'); // 'comunidad' | 'mis_posts' | 'mensajes'
    const [showModal, setShowModal] = useState(false);
    const [selectedChat, setSelectedChat] = useState(null);
    const [expandedPostId, setExpandedPostId] = useState(null);

    useEffect(() => {
        if (!userData || !userData.condominioId) return;

        if (activeTab === 'mensajes') {
            setLoading(false);
            return;
        }

        setLoading(true);
        const postsRef = collection(db, 'muro_posts');
        let q;

        if (activeTab === 'comunidad') {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

            q = query(
                postsRef,
                where('condominioId', '==', userData.condominioId),
                where('estatus', '==', 'aprobado'),
                where('fechaCreacion', '>=', thirtyDaysAgo),
                orderBy('fechaCreacion', 'desc')
            );
        } else {
            q = query(
                postsRef,
                where('usuarioId', '==', currentUser.uid),
                orderBy('fechaCreacion', 'desc')
            );
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const postsData = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(p => activeTab === 'mis_posts' ? p.estatus !== 'cancelado' : true);

            setPosts(postsData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching posts:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userData, activeTab, currentUser]);

    const handleLogout = async () => {
        try { await logout(); navigate('/login'); } catch (e) { console.error(e); }
    };

    const handleCancelPost = async (postId) => {
        if (!window.confirm("¿Seguro que deseas cancelar esta publicación? Desaparecerá del muro.")) return;
        try {
            await updateDoc(doc(db, 'muro_posts', postId), {
                estatus: 'cancelado'
            });
        } catch (error) {
            console.error("Error cancelling post:", error);
            alert("Error al cancelar.");
        }
    };

    const handleLike = async (post) => {
        if (!currentUser) return;
        const postRef = doc(db, 'muro_posts', post.id);
        const hasLiked = post.reacciones && post.reacciones.includes(currentUser.uid);

        try {
            if (hasLiked) {
                await updateDoc(postRef, {
                    likesCount: increment(-1),
                    reacciones: arrayRemove(currentUser.uid)
                });
            } else {
                await updateDoc(postRef, {
                    likesCount: increment(1),
                    reacciones: arrayUnion(currentUser.uid)
                });
            }
        } catch (error) {
            console.error("Error toggling like:", error);
        }
    };

    const handleContact = async (post) => {
        if (post.usuarioId === currentUser.uid) {
            alert("No puedes contactarte a ti mismo.");
            return;
        }

        try {
            const chatsRef = collection(db, 'muro_chats');
            const q = query(chatsRef, where('participants', 'array-contains', currentUser.uid));
            const snapshot = await getDocs(q);

            let existingChat = snapshot.docs.find(doc => {
                const data = doc.data();
                return data.postId === post.id && data.participants.includes(post.usuarioId);
            });

            if (existingChat) {
                setSelectedChat({ id: existingChat.id, ...existingChat.data() });
            } else {
                const newChatData = {
                    participants: [currentUser.uid, post.usuarioId],
                    postId: post.id,
                    postTitle: post.titulo,
                    lastMessage: '',
                    lastMessageTime: serverTimestamp(),
                    residentNames: {
                        [currentUser.uid]: userData.nombre || "Usuario",
                        [post.usuarioId]: post.usuarioNombre || "Usuario"
                    }
                };
                const docRef = await addDoc(collection(db, 'muro_chats'), newChatData);
                setSelectedChat({ id: docRef.id, ...newChatData });
            }

            setActiveTab('mensajes');

        } catch (error) {
            console.error("Error creating chat:", error);
            alert("Error al abrir chat.");
        }
    };

    const togglePost = (postId) => {
        setExpandedPostId(prev => prev === postId ? null : postId);
    };

    if (authLoading) return <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 50 }}>Cargando...</div>;

    return (
        <div className="ks-page">
            {/* ── DESKTOP SIDEBAR ─────────────────────── */}
            <KondoSidebar activeRoute="/muro" />

            {/* ── MAIN AREA ────────────────────────────── */}
            <main className="ks-main">
                <div className="ks-scroll-area">
                    <div className="ks-muro-header-sticky-wrapper">
                        <KondoBrandHeader showBack={false} />
                    </div>

                    <div className="muro-header-stitch">
                        <div className="muro-hero-card">
                            <div className="muro-hero-content">
                                <h1>Muro Social</h1>
                                <p>Conecta con tus vecinos y mantente al día con la comunidad.</p>
                                <button className="muro-hero-action-btn" onClick={() => setShowModal(true)}>
                                    <IoAdd size={20} /> Publicar
                                </button>
                            </div>
                            <IoChatbubblesOutline className="muro-hero-icon-decor" />
                        </div>
                    </div>

                    <div className="muro-content-container">
                        <div className="premium-tabs-bar-stitch">
                            <button className={`tab-stitch ${activeTab === 'comunidad' ? 'active' : ''}`} onClick={() => setActiveTab('comunidad')}>
                                <IoAppsOutline size={22} className="tab-icon" />
                                <span>FEED</span>
                            </button>
                            <button className={`tab-stitch ${activeTab === 'mis_posts' ? 'active' : ''}`} onClick={() => setActiveTab('mis_posts')}>
                                <IoDocumentTextOutline size={22} className="tab-icon" />
                                <span>MIS POSTS</span>
                            </button>
                            <button className={`tab-stitch ${activeTab === 'mensajes' ? 'active' : ''}`} onClick={() => setActiveTab('mensajes')}>
                                <IoChatbubblesOutline size={22} className="tab-icon" />
                                <span>MENSAJES</span>
                            </button>
                        </div>

                        {activeTab === 'mensajes' ? (
                            selectedChat ? (
                                <MuroChatWindow chat={selectedChat} onBack={() => setSelectedChat(null)} />
                            ) : (
                                <MuroChatList onSelectChat={(chat) => setSelectedChat(chat)} />
                            )
                        ) : (
                            loading ? (
                                <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Cargando...</div>
                            ) : posts.length === 0 ? (
                                <div className="empty-state" style={{ marginTop: 20 }}>
                                    <IoAlertCircleOutline size={48} color="#9ca3af" />
                                    <h3 style={{ margin: '15px 0 5px' }}>{activeTab === 'comunidad' ? "El muro está vacío" : "No tienes publicaciones"}</h3>
                                    <p style={{ color: '#6b7280' }}>{activeTab === 'comunidad' ? "¿Por qué no inicias la conversación?" : "Crea tu primer aviso hoy."}</p>
                                </div>
                            ) : (
                                <div className="muro-feed-grid">
                                    {posts.map(post => {
                                        const isExpanded = expandedPostId === post.id;
                                        return (
                                            <div key={post.id} className={`post-card-stitch ${isExpanded ? 'expanded' : ''}`} onClick={() => togglePost(post.id)}>
                                                <div className="post-header-stitch">
                                                    <div className="post-author-row">
                                                        <div className="post-avatar-stitch">{(post.usuarioNombre || "U").charAt(0).toUpperCase()}</div>
                                                        <div className="post-meta-stitch">
                                                            <span className="post-name-stitch">{post.usuarioNombre}</span>
                                                            <span className="post-date-stitch">{post.fechaCreacion?.toDate ? moment(post.fechaCreacion.toDate()).fromNow() : "Reciente"}</span>
                                                        </div>
                                                    </div>
                                                    <CategoryBadge type={post.tipo} />
                                                </div>
                                                <div className="post-content-stitch">
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                                        <div className="post-title-stitch">{post.titulo}</div>
                                                        {post.tipo === 'venta' && post.precio && <div className="post-price-badge-stitch">${post.precio}</div>}
                                                    </div>
                                                    <div className="post-body-stitch">{post.contenido}</div>
                                                </div>
                                                {post.imagenUrl && (
                                                    <div className="post-image-container-stitch" onClick={e => e.stopPropagation()}>
                                                        <img src={post.imagenUrl} alt="Post" className="post-image-stitch" loading="lazy" />
                                                    </div>
                                                )}
                                                <div className="post-footer-stitch" onClick={e => e.stopPropagation()}>
                                                    <div className="post-interactions-stitch" onClick={(e) => { e.stopPropagation(); handleLike(post); }} style={{ cursor: 'pointer' }}>
                                                        {post.reacciones && post.reacciones.includes(currentUser.uid) ? (
                                                            <IoHeart size={20} className="interaction-icon" style={{ color: '#ef4444' }} />
                                                        ) : (
                                                            <IoHeartOutline size={20} className="interaction-icon" style={{ color: (post.likesCount || 0) > 0 ? '#ef4444' : '#6b7280' }} />
                                                        )}
                                                        {(post.likesCount || 0) > 0 && <span className="interaction-counter">{post.likesCount}</span>}
                                                    </div>
                                                    {activeTab === 'mis_posts' ? (
                                                        <div className="post-footer-actions-right">
                                                            {post.estatus === 'pendiente' && <span className="status-indicator-stitch status-pendiente"><IoTimeOutline /> Revisión</span>}
                                                            {post.estatus === 'aprobado' && <span className="status-indicator-stitch status-publicado"><IoCheckmarkCircleOutline /> Activo</span>}
                                                            {post.estatus === 'rechazado' && <span className="status-indicator-stitch status-rechazado"><IoCloseCircleOutline /> Rechazado</span>}
                                                            <button onClick={(e) => { e.stopPropagation(); handleCancelPost(post.id); }} className="action-btn-danger">
                                                                <IoTrashOutline size={18} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        post.usuarioId !== currentUser.uid && (
                                                            <button onClick={(e) => { e.stopPropagation(); handleContact(post); }} className="contact-btn-stitch">Contactar</button>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* ── FLOATING ISLAND BOTTOM NAV (mobile) ── */}
                <KondoBottomNav activeRoute="/muro" />
            </main>

            {showModal && (
                <CrearPostModal
                    onClose={() => setShowModal(false)}
                    onSuccess={() => { setActiveTab('mis_posts'); }}
                />
            )}
        </div>
    );
};

export default MuroScreen;
